import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Clock, X } from 'lucide-react';
import { useRecoilState } from 'recoil';
import { workflowNodesState } from '../state/workflowState';
import { useTranslation } from 'react-i18next';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/shared/components/ui/alert-dialog';

interface ScheduleWaitingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string;
  scheduleData: {
    cronExpression?: string;
    scheduledTime?: string;
    timezone?: string;
    triggerId?: string;
    message?: string;
  };
  onStopWorkflow?: () => void;
}

const ScheduleWaitingDialog: React.FC<ScheduleWaitingDialogProps> = ({
  isOpen,
  onClose,
  nodeId,
  scheduleData,
  onStopWorkflow,
}) => {
  const { t, i18n } = useTranslation();
  const [, setNodes] = useRecoilState(workflowNodesState);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  // Map i18n language to locale format
  const getLocale = () => {
    const lang = i18n.language;
    if (lang.startsWith('pt')) return 'pt-BR';
    if (lang.startsWith('en')) return 'en-US';
    return 'en-US'; // Default fallback
  };

  const updateNodeLoading = (nodeId: string, isLoading: boolean) => {
    setNodes((currentNodes) =>
      currentNodes.map((node) =>
        node.id === nodeId
          ? { ...node, data: { ...node.data, isLoading: isLoading } }
          : node
      )
    );
  };



  useEffect(() => {
    console.log('ScheduleWaitingDialog recebeu props:', {
      isOpen,
      nodeId,
      scheduleData
    });
  }, [isOpen, nodeId, scheduleData]);

  // Reset estados internos quando o modal for fechado
  useEffect(() => {
    if (!isOpen) {
      setIsConfirmDialogOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && nodeId) {
      console.log('Diálogo de espera de schedule aberto para o node:', nodeId);
      updateNodeLoading(nodeId, true);
    }

    return () => {
      if (nodeId) {
        updateNodeLoading(nodeId, false);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, nodeId]);

  const handleCloseRequest = () => {
    console.log('Fechamento do diálogo solicitado');
    
    if (onStopWorkflow) {
      console.log('Abrindo diálogo de confirmação para parar workflow');
      setIsConfirmDialogOpen(true);
    } else {
      console.log('Fechando diálogo sem confirmação (sem onStopWorkflow)');
      onClose();
    }
  };

  const handleConfirmStop = () => {
    console.log('Confirmação para parar workflow recebida');
    setIsConfirmDialogOpen(false);
    
    if (onStopWorkflow) {
      onStopWorkflow();
    }
    onClose();
  };

  const handleCancelClose = () => {
    setIsConfirmDialogOpen(false);
  };
  


  return (
    <>
      <Dialog 
        open={isOpen} 
        onOpenChange={(open) => {
          if (!open) {
            console.log('🔴 Dialog recebeu sinal para fechar! Usando handleCloseRequest em vez de fechamento direto');
            handleCloseRequest();
            return false;
          }
          return true;
        }}
      >
        <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-schedule-waiting-node={nodeId}>
          <button 
            onClick={handleCloseRequest}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
          
          <div className="pt-6 px-6">
            <h2 className="text-lg font-semibold text-center">{t('workflow.schedule.active')}</h2>
          </div>
          
          <div className="flex flex-col items-center space-y-4 p-6 pt-4">
            {/* Label contextual */}
            <h3 className="text-sm uppercase text-muted-foreground tracking-wider font-normal">
              {t('workflow.schedule.nextExecution')}
            </h3>
            
            {/* Ícone de relógio com animação - menor e mais sutil */}
            <div className="text-blue-500 -mt-2">
              <Clock className="h-12 w-12 text-primary opacity-80 animate-pulse" />
            </div>
            
            {/* Data/Hora da próxima execução - clara e específica */}
            <div className="text-center space-y-1 -mt-1">
              {scheduleData?.scheduledTime ? (
                <>
                  <div className="text-sm text-muted-foreground">
                    {new Date(scheduleData.scheduledTime).toLocaleDateString(getLocale())}
                  </div>
                  <div className="text-2xl font-semibold">
                    {new Date(scheduleData.scheduledTime).toLocaleTimeString(getLocale(), {
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })}
                  </div>
                </>
              ) : (
                <div className="text-xl font-semibold">
                  {t('common.loading')}
                </div>
              )}
            </div>
            
            {/* Seção informativa sobre o agendamento */}
            {scheduleData?.cronExpression && (
              <div className="w-full mt-2">
                <h4 className="text-xs uppercase text-muted-foreground tracking-wider mb-2 text-center font-normal">
                  {t('workflow.schedule.config')}
                </h4>
                
                <div className="space-y-2">
                  {/* Expressão Cron */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t('workflow.schedule.expression')}</span>
                    <code className="bg-muted px-2 py-1 rounded text-xs font-mono">
                      {scheduleData.cronExpression}
                    </code>
                  </div>
                  
                  {/* Timezone */}
                  {scheduleData.timezone && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{t('workflow.schedule.timezone')}</span>
                      <span className="font-medium">{scheduleData.timezone}</span>
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  {t('workflow.schedule.autoExecute')}
                </p>
              </div>
            )}
          </div>
          
          
          <div className="flex justify-end p-3 bg-muted border-t">
            <Button variant="outline" onClick={handleCloseRequest}>
              {t('common.actions.cancel')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Diálogo de confirmação para parar workflow */}
      <AlertDialog open={isConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('workflow.schedule.stop')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('workflow.schedule.stopConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancelClose}>
              {t('common.actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmStop} className="bg-red-600 hover:bg-red-700">
              {t('workflow.schedule.stop')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ScheduleWaitingDialog;
